import { Action } from 'redux';
import { PendingAgreementSignature } from 'types/Agreement';
import { DaycareAvailabilityDate } from 'types/Availability';
import { Customer } from 'types/Customer';
import { MarketplaceServiceCategory } from 'types/Marketplace';
import { Pet } from 'types/Pet';
import { Staff } from 'types/Staff';
import { OnlineBookingState } from 'reducers/onlineBooking/OnlineBookingState';
import {
    CheckoutView,
    OnlineBookingItem,
    OnlineBookingStep,
    SessionBusinessArea
} from 'types/OnlineBooking';
import { WalkingAvailability, WalkingEligibility, WalkingStaffAvailability } from 'types/Walking';

// Reset
export const ONLINE_BOOKING_RESET = 'online_booking/reset';
interface OnlineBookingResetAction extends Action<typeof ONLINE_BOOKING_RESET> {}

// Error
export const ONLINE_BOOKING_ERROR = 'online_booking/error';
interface OnlineBookingErrorAction extends Action<typeof ONLINE_BOOKING_ERROR> {
    payload?: Partial<OnlineBookingState>;
}

// Start over: clears the cart but keeps the authenticated customer
export const ONLINE_BOOKING_START_OVER = 'online_booking/start_over';
interface OnlineBookingStartOverAction extends Action<typeof ONLINE_BOOKING_START_OVER> {}

// Initialization
export const ONLINE_BOOKING_INIT = 'online_booking/init';
interface OnlineBookingInitAction extends Action<typeof ONLINE_BOOKING_INIT> {}

export const ONLINE_BOOKING_INIT_SUCCESS = 'online_booking/init_success';
interface OnlineBookingInitSuccessAction extends Action<typeof ONLINE_BOOKING_INIT_SUCCESS> {}

/*
 * Step
 */
export const ONLINE_BOOKING_NAVIGATE_TO_STEP = 'online_booking/navigate_to_step';

interface OnlineBookingNavigateToStepAction extends Action<typeof ONLINE_BOOKING_NAVIGATE_TO_STEP> {
    payload: {
        step: OnlineBookingStep;
    };
}

/*
 * Checkout view
 */
export const ONLINE_BOOKING_SET_CHECKOUT_VIEW = 'online_booking/set_checkout_view';

interface OnlineBookingSetCheckoutViewAction
    extends Action<typeof ONLINE_BOOKING_SET_CHECKOUT_VIEW> {
    payload: {
        view: CheckoutView;
    };
}

/*
 * Customer
 */
export const ONLINE_BOOKING_CREATE_CUSTOMER_START = 'online_booking/create_customer_start';
interface OnlineBookingCreateCustomerStartAction
    extends Action<typeof ONLINE_BOOKING_CREATE_CUSTOMER_START> {
    payload?: {
        customer: Customer;
    };
}

export const ONLINE_BOOKING_CREATE_CUSTOMER_SUCCESS = 'online_booking/create_customer_success';
interface OnlineBookingCreateCustomerSuccessAction
    extends Action<typeof ONLINE_BOOKING_CREATE_CUSTOMER_SUCCESS> {
    payload: {
        customer: Customer;
    };
}

export const ONLINE_BOOKING_AUTHENTICATE_CUSTOMER_START =
    'online_booking/authenticate_customer_start';
interface OnlineBookingAuthenticateCustomerStartAction
    extends Action<typeof ONLINE_BOOKING_AUTHENTICATE_CUSTOMER_START> {}

export const ONLINE_BOOKING_AUTHENTICATE_CUSTOMER_SUCCESS =
    'online_booking/authenticate_customer_success';
interface OnlineBookingAuthenticateCustomerSuccessAction
    extends Action<typeof ONLINE_BOOKING_AUTHENTICATE_CUSTOMER_SUCCESS> {
    payload: {
        customer: Customer;
    };
}

export const ONLINE_BOOKING_SET_CUSTOMER = 'online_booking/set_customer';
interface OnlineBookingSetCustomerAction extends Action<typeof ONLINE_BOOKING_SET_CUSTOMER> {
    payload: {
        customer: Customer;
    };
}

/*
 * Pets
 */
export const ONLINE_BOOKING_SAVE_PET_START = 'online_booking/save_pet_start';
interface OnlineBookingSavePetStartAction extends Action<typeof ONLINE_BOOKING_SAVE_PET_START> {}

export const ONLINE_BOOKING_SAVE_PET_SUCCESS = 'online_booking/save_pet_success';
interface OnlineBookingSavePetSuccessAction extends Action<typeof ONLINE_BOOKING_SAVE_PET_SUCCESS> {
    payload: {
        pet: Pet;
    };
}

export const ONLINE_BOOKING_SET_SELECTED_PETS = 'online_booking/set_selected_pets';
interface OnlineBookingSetSelectedPetsAction
    extends Action<typeof ONLINE_BOOKING_SET_SELECTED_PETS> {
    payload: {
        pets: Array<Pet>;
    };
}

/*
 * Booking items
 */
export const ONLINE_BOOKING_SET_BOOKING_ITEMS = 'online_booking/set_booking_items';
interface OnlineBookingSetBookingItemsAction
    extends Action<typeof ONLINE_BOOKING_SET_BOOKING_ITEMS> {
    payload: {
        items: Array<OnlineBookingItem>;
    };
}

export const ONLINE_BOOKING_UPDATE_BOOKING_ITEM = 'online_booking/update_booking_item';
interface OnlineBookingUpdateBookingItemAction
    extends Action<typeof ONLINE_BOOKING_UPDATE_BOOKING_ITEM> {
    payload: {
        uuid: string;
        options: Partial<OnlineBookingItem>;
    };
}

export const ONLINE_BOOKING_UPDATE_BOOKING_ITEMS = 'online_booking/update_booking_items';
interface OnlineBookingUpdateBookingItemsAction
    extends Action<typeof ONLINE_BOOKING_UPDATE_BOOKING_ITEMS> {
    payload: {
        items: Array<OnlineBookingItem>;
    };
}

export const ONLINE_BOOKING_REMOVE_BOOKING_ITEMS = 'online_booking/remove_booking_items';
interface OnlineBookingRemoveBookingItemsAction
    extends Action<typeof ONLINE_BOOKING_REMOVE_BOOKING_ITEMS> {
    payload: {
        uuids: Array<string>;
    };
}

/*
 * Selected category
 */
export const ONLINE_BOOKING_SET_SELECTED_CATEGORY = 'online_booking/set_selected_category';
interface OnlineBookingSetSelectedCategoryAction
    extends Action<typeof ONLINE_BOOKING_SET_SELECTED_CATEGORY> {
    payload: {
        category: MarketplaceServiceCategory;
    };
}

export const ONLINE_BOOKING_SET_EVALUATION_SERVICE_CATEGORY =
    'online_booking/set_evaluation_service_category';
interface OnlineBookingSetEvaluationServiceCategoryAction
    extends Action<typeof ONLINE_BOOKING_SET_EVALUATION_SERVICE_CATEGORY> {
    payload: {
        category?: MarketplaceServiceCategory;
    };
}

/*
 * Selected staff
 */
export const ONLINE_BOOKING_SET_SELECTED_STAFF = 'online_booking/set_selected_staff';
interface OnlineBookingSetSelectedStaffAction
    extends Action<typeof ONLINE_BOOKING_SET_SELECTED_STAFF> {
    payload: {
        staff?: Staff;
    };
}

/*
 * Availability
 */
export const ONLINE_BOOKING_GET_GROOMING_STAFF_NEXT_AVAILABLE_DATE_START =
    'online_booking/get_grooming_staff_next_available_date_start';
interface OnlineBookingGetGroomingStaffNextAvailableDateStartAction
    extends Action<typeof ONLINE_BOOKING_GET_GROOMING_STAFF_NEXT_AVAILABLE_DATE_START> {}

export const ONLINE_BOOKING_GET_GROOMING_STAFF_NEXT_AVAILABLE_DATE_SUCCESS =
    'online_booking/get_grooming_staff_next_available_date_success';
interface OnlineBookingGetGroomingStaffNextAvailableDateSuccessAction
    extends Action<typeof ONLINE_BOOKING_GET_GROOMING_STAFF_NEXT_AVAILABLE_DATE_SUCCESS> {}

export const ONLINE_BOOKING_GET_GROOMING_STAFF_AVAILABILITY_START =
    'online_booking/get_grooming_staff_availability_start';
interface OnlineBookingGetGroomingStaffAvailabilityStartAction
    extends Action<typeof ONLINE_BOOKING_GET_GROOMING_STAFF_AVAILABILITY_START> {}

export const ONLINE_BOOKING_GET_GROOMING_STAFF_AVAILABILITY_SUCCESS =
    'online_booking/get_grooming_staff_availability_success';
interface OnlineBookingGetGroomingStaffAvailabilitySuccessAction
    extends Action<typeof ONLINE_BOOKING_GET_GROOMING_STAFF_AVAILABILITY_SUCCESS> {}

export const ONLINE_BOOKING_GET_DAYCARE_AVAILABILITY_START =
    'online_booking/get_daycare_availability_start';
interface OnlineBookingGetDaycareAvailabilityStartAction
    extends Action<typeof ONLINE_BOOKING_GET_DAYCARE_AVAILABILITY_START> {
    payload: {
        dateKey: string;
        requestKey: string;
    };
}

export const ONLINE_BOOKING_GET_DAYCARE_AVAILABILITY_SUCCESS =
    'online_booking/get_daycare_availability_success';
interface OnlineBookingGetDaycareAvailabilitySuccessAction
    extends Action<typeof ONLINE_BOOKING_GET_DAYCARE_AVAILABILITY_SUCCESS> {
    payload: {
        dateKey: string;
        requestKey: string;
        availability: DaycareAvailabilityDate;
    };
}

export const ONLINE_BOOKING_GET_DAYCARE_AVAILABILITY_FAILED =
    'online_booking/get_daycare_availability_failed';
interface OnlineBookingGetDaycareAvailabilityFailedAction
    extends Action<typeof ONLINE_BOOKING_GET_DAYCARE_AVAILABILITY_FAILED> {
    payload: {
        dateKey: string;
        requestKey: string;
    };
}

export const ONLINE_BOOKING_CLEAR_DAYCARE_AVAILABILITY =
    'online_booking/clear_daycare_availability';
interface OnlineBookingClearDaycareAvailabilityAction
    extends Action<typeof ONLINE_BOOKING_CLEAR_DAYCARE_AVAILABILITY> {}

export const ONLINE_BOOKING_GET_BOARDING_AVAILABILITY_START =
    'online_booking/get_boarding_availability_start';
interface OnlineBookingGetBoardingAvailabilityStartAction
    extends Action<typeof ONLINE_BOOKING_GET_BOARDING_AVAILABILITY_START> {
    payload: {
        requestKey: string;
    };
}

export const ONLINE_BOOKING_GET_BOARDING_AVAILABILITY_SUCCESS =
    'online_booking/get_boarding_availability_success';
interface OnlineBookingGetBoardingAvailabilitySuccessAction
    extends Action<typeof ONLINE_BOOKING_GET_BOARDING_AVAILABILITY_SUCCESS> {
    payload: {
        requestKey: string;
        availability: DaycareAvailabilityDate;
    };
}

export const ONLINE_BOOKING_GET_BOARDING_AVAILABILITY_FAILED =
    'online_booking/get_boarding_availability_failed';
interface OnlineBookingGetBoardingAvailabilityFailedAction
    extends Action<typeof ONLINE_BOOKING_GET_BOARDING_AVAILABILITY_FAILED> {
    payload: {
        requestKey: string;
    };
}

export const ONLINE_BOOKING_CLEAR_BOARDING_AVAILABILITY =
    'online_booking/clear_boarding_availability';
interface OnlineBookingClearBoardingAvailabilityAction
    extends Action<typeof ONLINE_BOOKING_CLEAR_BOARDING_AVAILABILITY> {}

export const ONLINE_BOOKING_GET_WALKING_ELIGIBILITY_START =
    'online_booking/get_walking_eligibility_start';
interface OnlineBookingGetWalkingEligibilityStartAction
    extends Action<typeof ONLINE_BOOKING_GET_WALKING_ELIGIBILITY_START> {
    payload: {
        businessArea: SessionBusinessArea;
        requestKey: string;
    };
}

export const ONLINE_BOOKING_GET_WALKING_ELIGIBILITY_SUCCESS =
    'online_booking/get_walking_eligibility_success';
interface OnlineBookingGetWalkingEligibilitySuccessAction
    extends Action<typeof ONLINE_BOOKING_GET_WALKING_ELIGIBILITY_SUCCESS> {
    payload: {
        businessArea: SessionBusinessArea;
        requestKey: string;
        data: WalkingEligibility;
    };
}

export const ONLINE_BOOKING_GET_WALKING_ELIGIBILITY_FAILED =
    'online_booking/get_walking_eligibility_failed';
interface OnlineBookingGetWalkingEligibilityFailedAction
    extends Action<typeof ONLINE_BOOKING_GET_WALKING_ELIGIBILITY_FAILED> {
    payload: {
        businessArea: SessionBusinessArea;
        requestKey: string;
        error?: string;
    };
}

export const ONLINE_BOOKING_CLEAR_WALKING_ELIGIBILITY = 'online_booking/clear_walking_eligibility';
interface OnlineBookingClearWalkingEligibilityAction
    extends Action<typeof ONLINE_BOOKING_CLEAR_WALKING_ELIGIBILITY> {}

export const ONLINE_BOOKING_GET_WALKING_STAFF_AVAILABILITY_START =
    'online_booking/get_walking_staff_availability_start';
interface OnlineBookingGetWalkingStaffAvailabilityStartAction
    extends Action<typeof ONLINE_BOOKING_GET_WALKING_STAFF_AVAILABILITY_START> {
    payload: {
        businessArea: SessionBusinessArea;
        requestKey: string;
    };
}

export const ONLINE_BOOKING_GET_WALKING_STAFF_AVAILABILITY_SUCCESS =
    'online_booking/get_walking_staff_availability_success';
interface OnlineBookingGetWalkingStaffAvailabilitySuccessAction
    extends Action<typeof ONLINE_BOOKING_GET_WALKING_STAFF_AVAILABILITY_SUCCESS> {
    payload: {
        businessArea: SessionBusinessArea;
        requestKey: string;
        data: WalkingStaffAvailability;
    };
}

export const ONLINE_BOOKING_GET_WALKING_STAFF_AVAILABILITY_FAILED =
    'online_booking/get_walking_staff_availability_failed';
interface OnlineBookingGetWalkingStaffAvailabilityFailedAction
    extends Action<typeof ONLINE_BOOKING_GET_WALKING_STAFF_AVAILABILITY_FAILED> {
    payload: {
        businessArea: SessionBusinessArea;
        requestKey: string;
        error?: string;
    };
}

export const ONLINE_BOOKING_GET_WALKING_AVAILABILITY_START =
    'online_booking/get_walking_availability_start';
interface OnlineBookingGetWalkingAvailabilityStartAction
    extends Action<typeof ONLINE_BOOKING_GET_WALKING_AVAILABILITY_START> {
    payload: {
        businessArea: SessionBusinessArea;
        requestKey: string;
    };
}

export const ONLINE_BOOKING_GET_WALKING_AVAILABILITY_SUCCESS =
    'online_booking/get_walking_availability_success';
interface OnlineBookingGetWalkingAvailabilitySuccessAction
    extends Action<typeof ONLINE_BOOKING_GET_WALKING_AVAILABILITY_SUCCESS> {
    payload: {
        businessArea: SessionBusinessArea;
        requestKey: string;
        data: WalkingAvailability;
    };
}

export const ONLINE_BOOKING_GET_WALKING_AVAILABILITY_FAILED =
    'online_booking/get_walking_availability_failed';
interface OnlineBookingGetWalkingAvailabilityFailedAction
    extends Action<typeof ONLINE_BOOKING_GET_WALKING_AVAILABILITY_FAILED> {
    payload: {
        businessArea: SessionBusinessArea;
        requestKey: string;
        error?: string;
    };
}

export const ONLINE_BOOKING_RECOVER_WALKING_BOOKING_ERROR =
    'online_booking/recover_walking_booking_error';
interface OnlineBookingRecoverWalkingBookingErrorAction
    extends Action<typeof ONLINE_BOOKING_RECOVER_WALKING_BOOKING_ERROR> {
    payload: {
        businessArea: SessionBusinessArea;
        code?: string;
        itemUuid?: string;
        availabilityKeysToRemove: Array<string>;
        staffAvailabilityKeysToRemove: Array<string>;
    };
}

/*
 * Agreements
 */
export const ONLINE_BOOKING_ADD_AGREEMENT_SIGNATURE = 'online_booking/add_agreement_signature';
interface OnlineBookingAddAgreementSignatureAction
    extends Action<typeof ONLINE_BOOKING_ADD_AGREEMENT_SIGNATURE> {
    payload: {
        signature: PendingAgreementSignature;
    };
}

export const ONLINE_BOOKING_AGREEMENT_SIGNATURE_PERSISTED =
    'online_booking/agreement_signature_persisted';
interface OnlineBookingAgreementSignaturePersistedAction
    extends Action<typeof ONLINE_BOOKING_AGREEMENT_SIGNATURE_PERSISTED> {
    payload: {
        agreementId: number;
    };
}

export const ONLINE_BOOKING_AGREEMENT_SIGNATURE_UPDATED =
    'online_booking/agreement_signature_updated';
interface OnlineBookingAgreementSignatureUpdatedAction
    extends Action<typeof ONLINE_BOOKING_AGREEMENT_SIGNATURE_UPDATED> {
    payload: {
        agreementId: number;
        changes: Pick<PendingAgreementSignature, 'signature' | 'saveFailed'>;
    };
}

/*
 * Notes
 */
export const ONLINE_BOOKING_SET_NOTES = 'online_booking/set_notes';
interface OnlineBookingSetNotesAction extends Action<typeof ONLINE_BOOKING_SET_NOTES> {
    payload: {
        notes: OnlineBookingState['notes'];
    };
}

/*
 * Bookings
 */
export const ONLINE_BOOKING_CREATE_BOOKING_START = 'online_booking/create_booking_start';
interface OnlineBookingCreateBookingStartAction
    extends Action<typeof ONLINE_BOOKING_CREATE_BOOKING_START> {}

export const ONLINE_BOOKING_CREATE_BOOKING_SUCCESS = 'online_booking/create_booking_success';
interface OnlineBookingCreateBookingSuccessAction
    extends Action<typeof ONLINE_BOOKING_CREATE_BOOKING_SUCCESS> {}

export const ONLINE_BOOKING_CONFIRM_BOOKING_DEPOSIT_START =
    'online_booking/confirm_booking_deposit_start';
interface OnlineBookingConfirmBookingDepositStartAction
    extends Action<typeof ONLINE_BOOKING_CONFIRM_BOOKING_DEPOSIT_START> {}

export const ONLINE_BOOKING_CONFIRM_BOOKING_DEPOSIT_SUCCESS =
    'online_booking/confirm_booking_deposit_success';
interface OnlineBookingConfirmBookingDepositSuccessAction
    extends Action<typeof ONLINE_BOOKING_CONFIRM_BOOKING_DEPOSIT_SUCCESS> {}

/*
 * Waitlist
 */
export const ONLINE_BOOKING_SET_WAITLIST_CATEGORY = 'online_booking/set_waitlist_category';
interface OnlineBookingSetWaitlistCategoryAction
    extends Action<typeof ONLINE_BOOKING_SET_WAITLIST_CATEGORY> {
    payload: {
        category?: MarketplaceServiceCategory;
    };
}

export const ONLINE_BOOKING_CREATE_WAITLIST_START = 'online_booking/create_waitlist_start';
interface OnlineBookingCreateWaitlistStartAction
    extends Action<typeof ONLINE_BOOKING_CREATE_WAITLIST_START> {}

export const ONLINE_BOOKING_CREATE_WAITLIST_SUCCESS = 'online_booking/create_waitlist_success';
interface OnlineBookingCreateWaitlistSuccessAction
    extends Action<typeof ONLINE_BOOKING_CREATE_WAITLIST_SUCCESS> {}

export type OnlineBookingAction =
    | OnlineBookingResetAction
    | OnlineBookingErrorAction
    | OnlineBookingStartOverAction
    // Initialization
    | OnlineBookingInitAction
    | OnlineBookingInitSuccessAction
    // Step
    | OnlineBookingNavigateToStepAction
    // Checkout view
    | OnlineBookingSetCheckoutViewAction
    // Customer
    | OnlineBookingCreateCustomerStartAction
    | OnlineBookingCreateCustomerSuccessAction
    | OnlineBookingAuthenticateCustomerStartAction
    | OnlineBookingAuthenticateCustomerSuccessAction
    | OnlineBookingSetCustomerAction
    // Pets
    | OnlineBookingSavePetStartAction
    | OnlineBookingSavePetSuccessAction
    | OnlineBookingSetSelectedPetsAction
    // Booking items
    | OnlineBookingSetBookingItemsAction
    | OnlineBookingUpdateBookingItemAction
    | OnlineBookingUpdateBookingItemsAction
    | OnlineBookingRemoveBookingItemsAction
    // Service category
    | OnlineBookingSetSelectedCategoryAction
    | OnlineBookingSetEvaluationServiceCategoryAction
    // Selected staff
    | OnlineBookingSetSelectedStaffAction
    // Availability
    | OnlineBookingGetGroomingStaffNextAvailableDateStartAction
    | OnlineBookingGetGroomingStaffNextAvailableDateSuccessAction
    | OnlineBookingGetGroomingStaffAvailabilityStartAction
    | OnlineBookingGetGroomingStaffAvailabilitySuccessAction
    | OnlineBookingGetDaycareAvailabilityStartAction
    | OnlineBookingGetDaycareAvailabilitySuccessAction
    | OnlineBookingGetDaycareAvailabilityFailedAction
    | OnlineBookingClearDaycareAvailabilityAction
    | OnlineBookingGetBoardingAvailabilityStartAction
    | OnlineBookingGetBoardingAvailabilitySuccessAction
    | OnlineBookingGetBoardingAvailabilityFailedAction
    | OnlineBookingClearBoardingAvailabilityAction
    | OnlineBookingGetWalkingEligibilityStartAction
    | OnlineBookingGetWalkingEligibilitySuccessAction
    | OnlineBookingGetWalkingEligibilityFailedAction
    | OnlineBookingClearWalkingEligibilityAction
    | OnlineBookingGetWalkingStaffAvailabilityStartAction
    | OnlineBookingGetWalkingStaffAvailabilitySuccessAction
    | OnlineBookingGetWalkingStaffAvailabilityFailedAction
    | OnlineBookingGetWalkingAvailabilityStartAction
    | OnlineBookingGetWalkingAvailabilitySuccessAction
    | OnlineBookingGetWalkingAvailabilityFailedAction
    | OnlineBookingRecoverWalkingBookingErrorAction
    // Agreements
    | OnlineBookingAddAgreementSignatureAction
    | OnlineBookingAgreementSignaturePersistedAction
    | OnlineBookingAgreementSignatureUpdatedAction
    // Notes
    | OnlineBookingSetNotesAction
    // Bookings
    | OnlineBookingCreateBookingStartAction
    | OnlineBookingCreateBookingSuccessAction
    | OnlineBookingConfirmBookingDepositStartAction
    | OnlineBookingConfirmBookingDepositSuccessAction
    // Waitlist
    | OnlineBookingSetWaitlistCategoryAction
    | OnlineBookingCreateWaitlistStartAction
    | OnlineBookingCreateWaitlistSuccessAction;
